import { useRef, useState } from "react";
import { useAuth } from "@clerk/react";
import { requestUploadUrl } from "@workspace/api-client-react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { MailCheck, Upload, FileIcon, X, Loader2 } from "lucide-react";

const ACCEPTED_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/gif",
  "image/webp",
];
const MAX_BYTES = 25 * 1024 * 1024;

function formatBytes(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

interface ReceiveResponseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contractId: number;
  roundId: number;
  roundNumber: number;
  recipientName: string | null;
  onSuccess: () => void;
}

export function ReceiveResponseDialog({
  open,
  onOpenChange,
  contractId,
  roundId,
  roundNumber,
  recipientName,
  onSuccess,
}: ReceiveResponseDialogProps) {
  const { getToken } = useAuth();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isPending, setIsPending] = useState(false);
  const [counterpartyComments, setCounterpartyComments] = useState("");
  const [returnedFile, setReturnedFile] = useState<File | null>(null);

  const handleClose = () => {
    if (!isPending) {
      onOpenChange(false);
      setCounterpartyComments("");
      setReturnedFile(null);
    }
  };

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!ACCEPTED_TYPES.includes(file.type)) {
      toast({
        title: "Unsupported file type",
        description: `${file.name} is not a supported type. Allowed: PDF, DOC, DOCX, PNG, JPG, GIF, WEBP.`,
        variant: "destructive",
      });
      return;
    }
    if (file.size > MAX_BYTES) {
      toast({
        title: "File too large",
        description: `${file.name} is ${formatBytes(file.size)}; maximum is ${formatBytes(MAX_BYTES)}.`,
        variant: "destructive",
      });
      return;
    }
    setReturnedFile(file);
  };

  const handleSubmit = async () => {
    setIsPending(true);
    try {
      let returnedDocumentObjectPath: string | undefined;
      if (returnedFile) {
        const presigned = await requestUploadUrl({
          name: returnedFile.name,
          size: returnedFile.size,
          contentType: returnedFile.type,
        });
        const putResp = await fetch(presigned.uploadURL, {
          method: "PUT",
          headers: {
            "Content-Type":
              (presigned as { requiredHeaders?: Record<string, string> })
                .requiredHeaders?.["Content-Type"] ?? returnedFile.type,
          },
          body: returnedFile,
        });
        if (!putResp.ok) throw new Error(`Upload failed: ${putResp.status}`);
        returnedDocumentObjectPath = presigned.objectPath;
      }

      const token = await getToken();
      const res = await fetch(`/api/contracts/${contractId}/negotiation-rounds/${roundId}/receive`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          counterpartyComments: counterpartyComments.trim() || undefined,
          returnedDocumentObjectPath,
          // Send the original filename / MIME type / size so the server can
          // store them and serve a faithful Content-Disposition on download.
          returnedDocumentFilename: returnedFile?.name,
          returnedDocumentContentType: returnedFile?.type || undefined,
          returnedDocumentSize: returnedFile?.size,
        }),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as {
          error?: string;
          code?: string;
          message?: string;
          detail?: string;
        };
        const code = err.code ?? err.error;
        if (res.status === 422 && code === "malware_detected") {
          toast({
            title: "File rejected — malware detected",
            description:
              err.message ??
              "The uploaded document was flagged by malware scanning and was not stored. Ask the counterparty to resend a clean copy.",
            variant: "destructive",
          });
          setReturnedFile(null);
          return;
        }
        if (res.status === 503 && code === "malware_scan_unavailable") {
          toast({
            title: "Malware scan unavailable",
            description:
              err.message ??
              "We couldn't scan the uploaded document for malware. Please retry; if the problem persists, contact support.",
            variant: "destructive",
          });
          return;
        }
        throw new Error(err.message ?? err.error ?? "Failed to mark round as received");
      }
      toast({
        title: "Response recorded",
        description: "The round is now marked as received. The holder has been flipped back to your team.",
      });
      onSuccess();
      handleClose();
    } catch (err) {
      toast({ title: "Error", description: err instanceof Error ? err.message : "Could not record response.", variant: "destructive" });
    } finally {
      setIsPending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MailCheck className="h-5 w-5 text-green-600" />
            Mark Round {roundNumber} Received
          </DialogTitle>
          <DialogDescription>
            Record that {recipientName ?? "the counterparty"} has responded to Round {roundNumber}. This will flip the holder back to your team and move the contract to "Counter Received."
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="counterparty-comments">Counterparty comments (optional)</Label>
            <Textarea
              id="counterparty-comments"
              placeholder="Paste their key points, redline summary, or overall notes here…"
              rows={4}
              value={counterpartyComments}
              onChange={(e) => setCounterpartyComments(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Returned redlined document (optional)</Label>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept={ACCEPTED_TYPES.join(",")}
              onChange={handleFileSelected}
              data-testid="input-returned-document"
            />
            {returnedFile ? (
              <div className="flex items-center gap-2 p-2 rounded-md border bg-muted/30">
                <FileIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate" title={returnedFile.name}>{returnedFile.name}</div>
                  <div className="text-xs text-muted-foreground">{formatBytes(returnedFile.size)}</div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => setReturnedFile(null)}
                  disabled={isPending}
                  data-testid="button-clear-returned-document"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={isPending}
                data-testid="button-pick-returned-document"
              >
                <Upload className="h-4 w-4 mr-2" />
                Choose file
              </Button>
            )}
            <p className="text-xs text-muted-foreground">
              Attach the counterparty's redlined version. PDF, DOC, DOCX, or images up to {formatBytes(MAX_BYTES)}.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isPending} className="bg-green-600 hover:bg-green-700">
            {isPending ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Recording…</>
            ) : (
              <><MailCheck className="h-4 w-4 mr-2" /> Mark as Received</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
